import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Play, Pause, RotateCcw, SkipForward } from "lucide-react";
import { toast } from "sonner";
import { Overline } from "@/components/ui-kit";

const BREAK = 5 * 60;
type Mode = "work" | "break";

const fmt = (s: number) => {
  const m = Math.floor(s / 60)
    .toString()
    .padStart(2, "0");
  const sec = (s % 60).toString().padStart(2, "0");
  return `${m}:${sec}`;
};

const Focus: React.FC = () => {
  const location = useLocation();
  const session = (location.state as { minutes?: number; label?: string } | null) || null;
  const workMin = session?.minutes && session.minutes > 0 ? session.minutes : 25;
  const WORK = workMin * 60;
  const label = session?.label ?? null;

  const [mode, setMode] = useState<Mode>("work");
  const [secondsLeft, setSecondsLeft] = useState(WORK);
  const [running, setRunning] = useState(false);
  const [cycles, setCycles] = useState(0);
  const intervalRef = useRef<number | null>(null);

  const total = mode === "work" ? WORK : BREAK;

  const switchMode = (next: Mode) => {
    setMode(next);
    setSecondsLeft(next === "work" ? WORK : BREAK);
    setRunning(false);
  };

  useEffect(() => {
    if (!running) return;
    intervalRef.current = window.setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) {
          if (navigator.vibrate) navigator.vibrate(120);
          if (mode === "work") {
            setCycles((c) => c + 1);
            toast.success("Session terminée. Prends une pause.");
            setMode("break");
            setRunning(false);
            return BREAK;
          }
          toast("Pause terminée. On repart ?");
          setMode("work");
          setRunning(false);
          return WORK;
        }
        return s - 1;
      });
    }, 1000);
    return () => {
      if (intervalRef.current) window.clearInterval(intervalRef.current);
    };
  }, [running, mode, WORK]);

  const reset = () => {
    setRunning(false);
    setSecondsLeft(total);
  };

  const R = 130;
  const C = 2 * Math.PI * R;
  const progress = secondsLeft / total;
  const offset = C * (1 - progress);

  return (
    <div className="flex flex-col items-center">
      <div className="w-full">
        <Overline className="mb-2">{mode === "work" ? "Temps de focus" : "Temps de pause"}</Overline>
        <h1 className="font-heading text-3xl sm:text-4xl font-light tracking-tight text-forest-900 leading-tight">
          {label ?? (mode === "work" ? "Une chose à la fois." : "Respire un instant.")}
        </h1>
        {label && mode === "work" && (
          <p className="mt-2 text-sm text-forest-500">Session de {workMin} minutes</p>
        )}
      </div>

      <div className="relative my-12 flex items-center justify-center" data-testid="timer-circle">
        <svg width="300" height="300" viewBox="0 0 300 300" className="-rotate-90">
          <circle cx="150" cy="150" r={R} fill="none" stroke="#212621" strokeOpacity="0.06" strokeWidth="6" />
          <motion.circle
            cx="150"
            cy="150"
            r={R}
            fill="none"
            stroke={mode === "work" ? "#8A9A86" : "#D4A373"}
            strokeWidth="6"
            strokeLinecap="round"
            strokeDasharray={C}
            initial={{ strokeDashoffset: C }}
            animate={{ strokeDashoffset: offset }}
            transition={{ duration: 0.5, ease: "linear" }}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="font-heading text-6xl font-light tracking-tight text-forest-900 tabular-nums" data-testid="timer-display">
            {fmt(secondsLeft)}
          </span>
          <span className="mt-2 text-xs uppercase tracking-[0.2em] text-forest-500">
            {cycles} session{cycles > 1 ? "s" : ""} · {workMin} / 5
          </span>
        </div>
      </div>

      <div className="flex items-center gap-4 mb-8">
        <button
          onClick={reset}
          data-testid="timer-reset"
          aria-label="Réinitialiser"
          className="h-14 w-14 rounded-full bg-white border border-forest-900/10 flex items-center justify-center text-forest-700"
        >
          <RotateCcw strokeWidth={1.75} className="h-5 w-5" />
        </button>

        <motion.button
          whileTap={{ scale: 0.94 }}
          onClick={() => setRunning((r) => !r)}
          data-testid="timer-toggle"
          className="h-20 w-20 rounded-full bg-sage text-white flex items-center justify-center shadow-[0_10px_30px_rgba(138,154,134,0.45)]"
        >
          {running ? (
            <Pause className="h-8 w-8 fill-current" />
          ) : (
            <Play className="h-8 w-8 fill-current ml-1" />
          )}
        </motion.button>

        <button
          onClick={() => switchMode(mode === "work" ? "break" : "work")}
          data-testid="timer-skip"
          aria-label="Passer"
          className="h-14 w-14 rounded-full bg-white border border-forest-900/10 flex items-center justify-center text-forest-700"
        >
          <SkipForward strokeWidth={1.75} className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
};

export default Focus;
